import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import InvitationDesignerV2 from '../_components/InvitationDesigner/InvitationDesignerV2';
import OnboardingWizard from '../_components/OnboardingWizard/OnboardingWizard';
import { Event, InvitationType } from '../_graphql/generated/operations';
import { InvitationDesign } from '../_types/invitation-design-v2';
import { eventDetailsApi } from '../_lib/api-client';

// Mock event for new users
const createMockEvent = (): Event => ({
  id: 'preview-event',
  eventName: 'Your Special Event',
  eventDateTime: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).getTime(), // 90 days from now
  createdAt: Date.now(),
  updatedAt: Date.now(),
  invitations: [],
  invitationTypes: []
});

// Mock invitation types
const createMockInvitationTypes = (): InvitationType[] => {
  const mockEvent = createMockEvent();
  return [
    {
      id: 'preview-save-date',
      eventId: 'preview-event',
      name: 'Save the Date',
      description: 'Early notification for your guests',
      designCustomizations: null,
      createdAt: Date.now(),
      updatedAt: Date.now(),
      event: mockEvent
    },
    {
      id: 'preview-full',
      eventId: 'preview-event',
      name: 'Full Invitation',
      description: 'Complete invitation with all details',
      designCustomizations: null,
      createdAt: Date.now(),
      updatedAt: Date.now(),
      event: mockEvent
    },
    {
      id: 'preview-ceremony',
      eventId: 'preview-event',
      name: 'Ceremony Only',
      description: 'For ceremony-only guests',
      designCustomizations: null,
      createdAt: Date.now(),
      updatedAt: Date.now(),
      event: mockEvent
    }
  ];
};

const InvitationDesignerOnboarding: React.FC = () => {
  const navigate = useNavigate();
  const { login, register, isAuthenticated } = useKindeAuth();
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [savedDesign, setSavedDesign] = useState<InvitationDesign | null>(null);
  const [selectedInvitationType, setSelectedInvitationType] = useState<InvitationType | null>(null);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showAnalyzing, setShowAnalyzing] = useState(false);
  const [suggestedEventName, setSuggestedEventName] = useState('');
  const [suggestedEventDate, setSuggestedEventDate] = useState('');
  const [eventName, setEventName] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [aiConfidence, setAiConfidence] = useState({ name: 0, date: 0 });
  const [showWizard, setShowWizard] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState<InvitationDesign | null>(null);

  // Create mock data
  const mockEvent = createMockEvent();
  const mockInvitationTypes = createMockInvitationTypes();
  mockEvent.invitationTypes = mockInvitationTypes;

  // Store design in localStorage for persistence
  useEffect(() => {
    const storedDesign = localStorage.getItem('onboarding-design');
    if (storedDesign) {
      setSavedDesign(JSON.parse(storedDesign));
    }
  }, []);


  const handleSave = async (design: InvitationDesign, invitationType?: InvitationType) => {
    console.log('Design saved:', design);
    setSavedDesign(design);
    setSelectedInvitationType(invitationType || null);
    
    // Store in localStorage
    localStorage.setItem('onboarding-design', JSON.stringify(design));
    
    // Show success briefly
    setShowSuccess(true);
    setTimeout(() => {
      setShowSuccess(false);
      // Show analyzing indicator after success message
      setShowAnalyzing(true);
    }, 2000);
    
    // Mark as interacted
    if (!hasInteracted) {
      setHasInteracted(true);
    }
    
    // Analyze content for event details (start after success message delay)
    setTimeout(async () => {
      setIsAnalyzing(true);
      try {
        // Use the backend API for analysis
        console.log('🤖 Using backend API for analysis...');
        const result = await eventDetailsApi.parseFromDesign(design);
        
        console.log('📊 Extraction results:', result);
        
        // Calculate confidence based on whether we got results
        const nameConfidence = result.eventName ? 0.9 : 0;
        const dateConfidence = result.eventDate ? 0.9 : 0;
        
        // Store confidence levels
        setAiConfidence({ name: nameConfidence, date: dateConfidence });
        
        if (result.eventName) {
          console.log('✅ Found event name:', result.eventName);
          setSuggestedEventName(result.eventName);
          setEventName(result.eventName);
        } else {
          console.log('❌ No event name found');
        }
        
        if (result.eventDate) {
          console.log('✅ Found event date:', result.eventDate);
          setSuggestedEventDate(result.eventDate);
          setEventDate(result.eventDate);
        } else {
          console.log('❌ No event date found');
        }
      } catch (error) {
        console.error('Error analyzing content:', error);
      } finally {
        setIsAnalyzing(false);
        setShowAnalyzing(false);
        // Show event creation modal after analysis is complete
        setShowEventModal(true);
      }
    }, 2000);
  };

  const handleCancel = () => {
    // Just close any prompts
    setShowAuthPrompt(false);
  };

  const handleRegister = () => {
    // Store current design state before redirecting
    if (savedDesign) {
      localStorage.setItem('onboarding-design', JSON.stringify(savedDesign));
    }
    register();
  };

  const handleLogin = () => {
    // Store current design and event details before redirecting
    if (savedDesign) {
      // Check if we have event details from the modal
      if (eventName || eventDate) {
        const onboardingData = {
          design: savedDesign,
          eventName: eventName,
          eventDate: eventDate
        };
        console.log('💾 Storing onboarding data before login:', {
          hasDesign: !!savedDesign,
          eventName: eventName,
          eventDate: eventDate
        });
        localStorage.setItem('onboarding-data', JSON.stringify(onboardingData));
      } else {
        // Fallback to old format
        localStorage.setItem('onboarding-design', JSON.stringify(savedDesign));
      }
    }
    login();
  };

  const handleWizardComplete = (invitationType: InvitationType, template: InvitationDesign) => {
    console.log('Wizard completed with:', { invitationType: invitationType.name, template: template.template.name });
    setSelectedInvitationType(invitationType);
    setSelectedTemplate(template);
    setSavedDesign(template);
    setShowWizard(false);
  };

  const handleContinueWithEvent = () => {
    if (isAuthenticated) {
      // For authenticated users, store the data and navigate directly to app
      const onboardingData = {
        design: savedDesign,
        eventName: eventName,
        eventDate: eventDate
      };
      console.log('💾 Storing onboarding data for authenticated user:', {
        hasDesign: !!savedDesign,
        eventName: eventName,
        eventDate: eventDate
      });
      localStorage.setItem('onboarding-data', JSON.stringify(onboardingData));
      
      // Navigate to app where the event will be auto-created
      navigate('/app');
    } else {
      // For non-authenticated users, store event details and proceed to registration
      const onboardingData = {
        design: savedDesign,
        eventName: eventName,
        eventDate: eventDate
      };
      console.log('💾 Storing onboarding data:', {
        hasDesign: !!savedDesign,
        eventName: eventName,
        eventDate: eventDate
      });
      localStorage.setItem('onboarding-data', JSON.stringify(onboardingData));
      
      // Verify storage
      const stored = localStorage.getItem('onboarding-data');
      console.log('✅ Verified storage:', stored ? 'Success' : 'Failed');
      
      // Proceed to registration
      register();
    }
  };

  if (showWizard) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white shadow-sm fixed top-0 w-full z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <button 
                onClick={() => navigate('/')}
                className="flex items-center gap-3 hover:opacity-80 transition-opacity"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-rose-400 to-pink-500 rounded-lg flex items-center justify-center shadow-md">
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                  </svg>
                </div>
                <h1 className="text-xl font-bold text-gray-800">
                  InviteWith<span className="text-rose-500">Love</span>
                </h1>
              </button>
            </div>
          </div>
        </div>
        
        {/* Wizard */}
        <div className="pt-16">
          <OnboardingWizard
            invitationTypes={mockInvitationTypes}
            onComplete={handleWizardComplete}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 relative">
      {/* Header */}
      <div className="bg-white shadow-sm relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button 
              onClick={() => navigate('/')}
              className="flex items-center gap-3 hover:opacity-80 transition-opacity"
            >
              <div className="w-10 h-10 bg-gradient-to-br from-rose-400 to-pink-500 rounded-lg flex items-center justify-center shadow-md">
                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                </svg>
              </div>
              <h1 className="text-xl font-bold text-gray-800">
                InviteWith<span className="text-rose-500">Love</span>
              </h1>
            </button>
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowWizard(true)}
                className="text-gray-700 hover:text-rose-600 font-medium transition-colors"
              >
                Change Template
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Success notification */}
      {showSuccess && (
        <div className="fixed top-20 right-4 bg-green-500 text-white px-6 py-3 rounded-md shadow-lg z-50 animate-fade-in">
          Great design! Let's save it to your account.
        </div>
      )}

      {/* Analyzing notification */}
      {showAnalyzing && (
        <div className="fixed top-20 right-4 bg-blue-500 text-white px-6 py-3 rounded-md shadow-lg z-50 flex items-center gap-3">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
          <span>Analyzing your invitation...</span>
        </div>
      )}

      {/* Event Creation Modal */}
      {showEventModal && (
        <div 
          className="fixed inset-0 flex items-center justify-center z-50 p-4"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.15)', backdropFilter: 'blur(2px)' }}
        >
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-8 relative">
            <button
              onClick={() => setShowEventModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-rose-400 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
                {isAnalyzing ? (
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                ) : (
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                )}
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2 text-center">
                Let's Create Your Event!
              </h3>
              <p className="text-gray-600 text-center">
                {isAnalyzing 
                  ? "Analyzing your invitation for event details..." 
                  : suggestedEventName || suggestedEventDate
                    ? "We found some details from your invitation. Feel free to adjust them!"
                    : "Tell us about your special event"}
              </p>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); handleContinueWithEvent(); }} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Event Name
                </label>
                <input
                  type="text"
                  value={eventName}
                  onChange={(e) => setEventName(e.target.value)}
                  placeholder="e.g., Sarah & Michael's Wedding"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                  required
                />
                {suggestedEventName && eventName === suggestedEventName && (
                  <p className="text-xs text-green-600 mt-1 flex items-center justify-between">
                    <span className="flex items-center">
                      <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      AI detected from your invitation
                    </span>
                    {aiConfidence.name > 0 && (
                      <span className="text-gray-500 text-xs">
                        {aiConfidence.name < 0.7 ? 'with corrections' : `${Math.round(aiConfidence.name * 100)}% confident`}
                      </span>
                    )}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Event Date
                </label>
                <input
                  type="date"
                  value={eventDate}
                  onChange={(e) => setEventDate(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                  required
                />
                {suggestedEventDate && eventDate === suggestedEventDate && (
                  <p className="text-xs text-green-600 mt-1 flex items-center justify-between">
                    <span className="flex items-center">
                      <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      AI detected from your invitation
                    </span>
                    {aiConfidence.date > 0 && (
                      <span className="text-gray-500 text-xs">
                        {aiConfidence.date < 0.7 ? 'year inferred' : `${Math.round(aiConfidence.date * 100)}% confident`}
                      </span>
                    )}
                  </p>
                )}
              </div>

              <div className="pt-4 space-y-3">
                <button
                  type="submit"
                  disabled={!eventName || !eventDate}
                  className="w-full bg-gradient-to-r from-rose-400 to-pink-500 text-white py-3 rounded-full font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isAuthenticated ? 'Create Event' : 'Continue to Sign Up'}
                </button>
                {!isAuthenticated && (
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={handleLogin}
                      className="flex-1 bg-white text-gray-700 py-2 rounded-full text-sm font-medium border border-gray-300 hover:border-gray-400 transition-all"
                    >
                      I Have an Account
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowEventModal(false)}
                      className="flex-1 text-gray-500 py-2 text-sm hover:text-gray-700 transition-colors"
                    >
                      Back to Design
                    </button>
                  </div>
                )}
                {isAuthenticated && (
                  <button
                    type="button"
                    onClick={() => setShowEventModal(false)}
                    className="w-full text-gray-500 py-2 text-sm hover:text-gray-700 transition-colors"
                  >
                    Back to Design
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Designer */}
      <div className="h-[calc(100vh-4rem)]">
        <InvitationDesignerV2
          event={mockEvent}
          onSave={handleSave}
          onCancel={handleCancel}
          initialDesign={savedDesign || selectedTemplate || undefined}
          selectedInvitationType={selectedInvitationType || undefined}
          hideTypeSelection={true}
          isOnboarding={true}
        />
      </div>
    </div>
  );
};

export default InvitationDesignerOnboarding;